import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { PlusCircle, Briefcase, AlertTriangle, Code2, Rocket, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { trackProductEvent } from "../lib/analytics";

/* ---------------- CONSTANTS ---------------- */

const POST_TYPES = [
  { key: "progress", label: "Progress", icon: Rocket },
  { key: "blocker", label: "Blocker", icon: AlertTriangle },
  { key: "development-tips", label: "Dev Tips", icon: Code2 },
  { key: "job-experience", label: "Job Experience", icon: Briefcase },
  { key: "job-referral", label: "Job Referral", icon: PlusCircle },
  { key: "looking-for-job", label: "Looking for Job", icon: Search },
  { key: "project-showcase", label: "Project Showcase", icon: Code2 },
];

const TECHNOLOGIES = [
  "react",
  "react-native",
  "firebase",
  "nodejs",
  "dsa",
  "system-design",
];

/* ---------------- STYLES ---------------- */

const Page = styled.div`
  min-height: 100vh;
  background: #020617;
  padding: 60px 16px;
  color: #e5e7eb;
`;

const Card = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 28px;
  border-radius: 16px;
  border: 1px solid rgba(99, 102, 241, 0.25);
  background: ${({ highlight }) =>
    highlight
      ? "linear-gradient(135deg, rgba(99,102,241,0.22), rgba(99,102,241,0.08))"
      : "rgba(15,23,42,0.55)"};
  backdrop-filter: blur(12px);
`;

const Title = styled.h2`
  margin: 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  margin-top: 4px;
`;

const TextArea = styled.textarea`
  width: 100%;
  background: #020617;
  border: 1px solid #1e293b;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 10px;
  outline: none;
  resize: vertical;
`;

const Input = styled.input`
  width: 100%;
  background: #020617;
  border: 1px solid #1e293b;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 10px;
  outline: none;
`;

const Section = styled.div`
  margin-top: 28px;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PostTypePill = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;

  background: ${({ active }) =>
    active
      ? "linear-gradient(135deg, rgba(99,102,241,0.22), rgba(99,102,241,0.08))"
      : "rgba(15,23,42,0.55)"};

  border: 1px solid
    ${({ active }) => (active ? "rgba(99,102,241,0.45)" : "rgba(30,41,59,1)")};

  color: ${({ active }) => (active ? "#c7d2fe" : "#94a3b8")};

  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.6);
  }
`;

const TechChip = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ active }) => (active ? "#38bdf8" : "transparent")};
  border: 1px solid #1e293b;
  color: ${({ active }) => (active ? "#020617" : "#94a3b8")};
`;

const CTA = styled.button`
  width: 100%;
  margin-top: 32px;
  padding: 14px;
  border-radius: 12px;
  font-weight: 600;
  border: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  background: ${({ disabled }) => (disabled ? "#1e293b" : "#38bdf8")};
  color: ${({ disabled }) => (disabled ? "#64748b" : "#020617")};
`;

/* ---------------- COMPONENT ---------------- */

export default function CreatePost() {
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user } = useAuth();
  const isEditMode = !!postId;

  const [loadingPost, setLoadingPost] = useState(!!postId);
  const [content, setContent] = useState("");
  const [postType, setPostType] = useState("");
  const [technologies, setTechnologies] = useState([]);
  const [extra, setExtra] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;

    const loadPost = async () => {
      if (!postId || !user) return;
      try {
        const postSnap = await getDoc(doc(db, "community_posts", postId));

        if (!postSnap.exists()) {
          navigate("/community/feeds", { replace: true });
          return;
        }

        const postData = postSnap.data();
        if (postData.uid !== user.uid) {
          navigate("/community/feeds", { replace: true });
          return;
        }

        if (!active) return;

        setContent(postData.content || "");
        setPostType(postData.postType || "");
        setTechnologies(postData.technologies || []);
        setExtra(postData.extra || {});
      } finally {
        if (active) setLoadingPost(false);
      }
    };

    loadPost();

    return () => {
      active = false;
    };
  }, [navigate, postId, user]);

  const toggleTech = (tech) => {
    setTechnologies((prev) => {
      const limit = postType === "looking-for-job" ? 3 : 2;
      if (prev.includes(tech)) return prev.filter((t) => t !== tech);
      if (prev.length >= limit) return prev;
      return [...prev, tech];
    });
  };

  const isValid = () => {
    if (!postType) return false;
    if (!content || content.length < 20) return false;

    if (postType === "blocker" && !extra.tried) return false;
    if (
      postType === "job-referral" &&
      (!extra.company || !extra.role || !extra.link)
    )
      return false;
    if (
      postType === "looking-for-job" &&
      (!extra.experience || !extra.role || !extra.proof)
    )
      return false;
    if (postType === "project-showcase" && !extra.projectLink) return false;

    return true;
  };

  const submitPost = async () => {
    if (!user) return;
    if (!isValid() || submitting) return;

    setSubmitting(true);

    try {
      if (isEditMode) {
        await updateDoc(doc(db, "community_posts", postId), {
          content,
          postType,
          technologies,
          extra,
          editedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "community_posts"), {
          uid: user.uid,
          userName: user.name || "Anonymous",
          content,
          postType,
          technologies,
          extra,
          createdAt: serverTimestamp(),
          editedAt: null,
          isDeleted: false,
          deletedAt: null,
          likes: [],
          comments: [],
        });
      }

      void trackProductEvent(
        isEditMode ? "community_post_updated" : "community_post_created",
        {
          postType,
          technologies,
        },
        { user }
      );

      navigate("/community/feeds");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card highlight={content.length > 0}>
        <Title>{isEditMode ? "Edit Community Post" : "Create Community Post"}</Title>
        <Subtitle>
          {isEditMode
            ? "Update your post and keep your community context current."
            : "Share progress, blockers, learnings or opportunities"}
        </Subtitle>
        {loadingPost ? <Subtitle>Loading your post...</Subtitle> : null}

        <Section>
          <TextArea
            rows={5}
            placeholder="What did you build, learn, or struggle with today?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={loadingPost}
          />
        </Section>

        <Section>
          <h4>Post Type</h4>
          <Pills>
            {POST_TYPES.map(({ key, label, icon: Icon }) => (
              <PostTypePill
                key={key}
                active={postType === key}
                type="button"
                onClick={() => {
                  if (loadingPost) return;
                  setPostType(key);
                  setExtra({});
                  setTechnologies([]);
                }}
              >
                <Icon size={16} />
                {label}
              </PostTypePill>
            ))}
          </Pills>
        </Section>

        {postType === "blocker" && (
          <Section>
            <TextArea
              placeholder="What have you already tried?"
              value={extra.tried || ""}
              onChange={(e) => setExtra({ ...extra, tried: e.target.value })}
            />
          </Section>
        )}

        {postType === "job-referral" && (
          <Section>
            <Input
              placeholder="Company"
              value={extra.company || ""}
              onChange={(e) => setExtra({ ...extra, company: e.target.value })}
            />
            <Input
              placeholder="Role"
              style={{ marginTop: 8 }}
              value={extra.role || ""}
              onChange={(e) => setExtra({ ...extra, role: e.target.value })}
            />
            <Input
              placeholder="Application / Contact link"
              style={{ marginTop: 8 }}
              value={extra.link || ""}
              onChange={(e) => setExtra({ ...extra, link: e.target.value })}
            />
          </Section>
        )}

        {postType === "looking-for-job" && (
          <Section>
            <Input
              placeholder="Experience"
              value={extra.experience || ""}
              onChange={(e) =>
                setExtra({ ...extra, experience: e.target.value })
              }
            />
            <Input
              placeholder="Target Role"
              style={{ marginTop: 8 }}
              value={extra.role || ""}
              onChange={(e) => setExtra({ ...extra, role: e.target.value })}
            />
            <Input
              placeholder="Proof (GitHub / Portfolio)"
              style={{ marginTop: 8 }}
              value={extra.proof || ""}
              onChange={(e) => setExtra({ ...extra, proof: e.target.value })}
            />
          </Section>
        )}

        {postType === "project-showcase" && (
          <Section>
            <Input
              placeholder="Project Link"
              value={extra.projectLink || ""}
              onChange={(e) =>
                setExtra({ ...extra, projectLink: e.target.value })
              }
            />
            <TextArea
              style={{ marginTop: 8 }}
              placeholder="What problem does it solve?"
              value={extra.problem || ""}
              onChange={(e) => setExtra({ ...extra, problem: e.target.value })}
            />
          </Section>
        )}

        <Section>
          <h4>Technologies</h4>
          <Pills>
            {TECHNOLOGIES.map((tech) => (
              <TechChip
                key={tech}
                active={technologies.includes(tech)}
                type="button"
                onClick={() => toggleTech(tech)}
              >
                {tech}
              </TechChip>
            ))}
          </Pills>
        </Section>

        <CTA disabled={!isValid() || loadingPost || submitting} onClick={submitPost}>
          {isEditMode ? "Save Changes" : "Publish Post"}
        </CTA>
      </Card>
    </Page>
  );
}
